import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useShop, api } from "@/store";
import { M, P } from "@/data/catalogue";
import PageHd from "@/components/PageHd";
import ProductCard from "@/components/ProductCard";
import { Reveal, MaskedLines } from "@/components/motion";

export function Wholesale() {
  const { t } = useShop();
  const w = (k) => t(`wholesale.${k}`);
  const [form, setForm] = useState({ name: "", company: "", email: "", programme: "", quantity: 25, message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api("/wholesale", { method: "POST", body: JSON.stringify({ ...form, quantity: Number(form.quantity) || 0, programme: form.programme || w("tiers")[0][0] }) });
      setSent(true);
    } catch {
      toast(w("err"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHd eb={w("eb")} h={w("h")} p={w("p")} testid="wholesale-title" />
      <div className="wrap">
        <div className="tiers">
          {w("tiers").map((x, i) => (
            <Reveal key={x[0]} delay={i * 0.08}>
              <div className={`tier${i === 1 ? " hi" : ""}`} style={{ height: "100%" }} data-testid={`tier-${i}`}>
                <div className="eyebrow">{x[1]}</div>
                <b>{x[0]}</b>
                <ul>{x.slice(2).map((y) => <li key={y}>{y}</li>)}</ul>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="split" style={{ marginTop: 70 }}>
          <Reveal><div className="im"><img src={M.gift} alt="Corporate gift set" loading="lazy" /></div></Reveal>
          <Reveal delay={0.12}>
            <div className="eyebrow">{w("form")}</div>
            <form className="form" style={{ marginTop: 16 }} onSubmit={submit} data-testid="wholesale-form">
              <div className="row">
                <label>{w("f")[0]}<input required data-testid="wholesale-name" value={form.name} onChange={set("name")} /></label>
                <label>{w("f")[1]}<input required data-testid="wholesale-company" value={form.company} onChange={set("company")} /></label>
              </div>
              <div className="row">
                <label>{w("f")[2]}<input type="email" required data-testid="wholesale-email" value={form.email} onChange={set("email")} /></label>
                <label>{w("f")[3]}
                  <select data-testid="wholesale-programme" value={form.programme || w("tiers")[0][0]} onChange={set("programme")}>
                    {w("tiers").map((x) => <option key={x[0]}>{x[0]}</option>)}
                  </select>
                </label>
              </div>
              <label>{w("f")[4]}<input type="number" min="12" data-testid="wholesale-quantity" value={form.quantity} onChange={set("quantity")} /></label>
              <label>{w("f")[5]}<textarea rows="3" data-testid="wholesale-message" value={form.message} onChange={set("message")} /></label>
              <button className="btn primary" type="submit" data-testid="wholesale-form-submit" disabled={busy || sent} style={{ justifySelf: "start" }}>
                {busy ? <span className="spin" /> : w("send")}
              </button>
              {sent && <div className="notice" data-testid="wholesale-success">{w("sent")}</div>}
            </form>
          </Reveal>
        </div>
      </div>
      <div style={{ height: 80 }} />
    </>
  );
}

export function Gifting() {
  const { t } = useShop();
  const g = (k) => t(`gifting.${k}`);
  return (
    <>
      <section className="hero" style={{ minHeight: "min(56vh,520px)" }}>
        <div className="copy">
          <div className="eyebrow">{g("eb")}</div>
          <MaskedLines lines={[g("h")]} testid="gifting-title" />
          <p className="lede">{g("p")}</p>
          <div className="cta">
            <Link className="btn light" to="/shop" data-testid="gifting-shop-cta">{t("coll.all")}</Link>
            <Link className="btn ghost" style={{ color: "#fff" }} to="/wholesale">{t("foot.corp")}</Link>
          </div>
        </div>
        <div className="media"><img className="bg" src={M.gift} alt="Nattiva gift box" /></div>
      </section>
      <section className="sec tight">
        <div className="wrap">
          <div className="pillars" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>
            {g("box").map((x, i) => (
              <Reveal key={x} delay={i * 0.07}>
                <div style={{ padding: 0 }}><span className="orn" /><p style={{ marginTop: 14 }}>{x}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <section className="sec" style={{ paddingTop: 0 }}>
        <div className="wrap grid g4">
          {P.filter((p) => !p.one).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.07}><ProductCard p={p} /></Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export function Press() {
  const { t } = useShop();
  const p = (k) => t(`press.${k}`);
  return (
    <>
      <PageHd eb={p("eb")} h={p("h")} testid="press-title" />
      <div className="wrap">
        <Reveal><div className="press">{p("items").map((x) => <div key={x[0]}>{x[0]}</div>)}</div></Reveal>
        <div className="grid g2" style={{ marginTop: 60 }}>
          {p("items").map((x, i) => (
            <Reveal key={x[0]} delay={i * 0.06}>
              <div style={{ borderTop: "1px solid var(--line)", paddingTop: 18 }} data-testid={`press-item-${i}`}>
                <div className="eyebrow mute">{x[0]} · {x[2]}</div>
                <p className="quote" style={{ fontSize: 26, margin: "10px 0 0", maxWidth: "none", textAlign: "left" }}>{x[1]}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div style={{ marginTop: 50, display: "flex", gap: 20, alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn" data-testid="press-kit-button" onClick={() => toast(p("soon"))}>{p("kit")}</button>
          <span style={{ color: "var(--fg-3)" }}>{p("contact")}</span>
        </div>
        <p className="notice" style={{ margin: "26px 0 80px" }}>{p("dis")}</p>
      </div>
    </>
  );
}

export function Careers() {
  const { t } = useShop();
  const c = (k) => t(`careers.${k}`);
  return (
    <>
      <PageHd eb={c("eb")} h={c("h")} p={c("p")} testid="careers-title" />
      <div className="wrap jobs" style={{ paddingBottom: 90, maxWidth: 900 }}>
        {c("jobs").map((j, i) => (
          <Reveal key={j[0]} delay={i * 0.06}>
            <a href={`mailto:careers@nattiva.co?subject=${encodeURIComponent(j[0])}`} data-testid={`job-${i}`} style={{ gridTemplateColumns: "1fr auto auto", display: "grid" }}>
              <strong style={{ fontWeight: 400, fontFamily: "var(--display)", fontSize: 24 }}>{j[0]}</strong>
              <span>{j[1]}</span>
              <span>{j[2]}</span>
            </a>
          </Reveal>
        ))}
      </div>
    </>
  );
}

export function Investors() {
  const { t } = useShop();
  const i = (k) => t(`investors.${k}`);
  return (
    <>
      <PageHd eb={i("eb")} h={i("h")} p={i("p")} testid="investors-title" />
      <div className="wrap" style={{ paddingBottom: 80 }}>
        <Reveal className="bigimg"><img src={M.dunes} alt="La Guajira dunes" loading="lazy" /></Reveal>
        <Reveal delay={0.1}>
          <div className="center" style={{ marginTop: 34 }}>
            <a className="btn" href="mailto:investors@nattiva.co?subject=Investor%20deck%20request" data-testid="investors-deck-button">{i("deck")}</a>
          </div>
        </Reveal>
      </div>
    </>
  );
}

export function Help() {
  const { t } = useShop();
  const h = (k) => t(`help.${k}`);
  return (
    <>
      <PageHd eb={h("eb")} h={h("h")} testid="help-title" />
      <div className="wrap faq" style={{ maxWidth: 820, paddingBottom: 90 }} data-testid="faq-list">
        {h("q").map((q, i) => (
          <details key={q[0]} id={q[0]}>
            <summary data-testid={`faq-${q[0]}`}>{q[1]}</summary>
            <div className="b">{q[2]}</div>
          </details>
        ))}
      </div>
    </>
  );
}

export function Contact() {
  const { t } = useShop();
  const c = (k) => t(`contact.${k}`);
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api("/contact", { method: "POST", body: JSON.stringify({ ...form, topic: form.topic || c("topics")[0] }) });
      setSent(true);
    } catch {
      toast(t("wholesale.err"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHd eb="Nattiva" h={c("h")} p={c("p")} testid="contact-title" />
      <div className="wrap split" style={{ alignItems: "start", paddingBottom: 80 }}>
        <Reveal className="bigimg" y={16}>
          <img src={M.cabo} alt="Cabo de la Vela" loading="lazy" style={{ aspectRatio: "4/3" }} />
        </Reveal>
        <Reveal delay={0.12}>
          <div className="eyebrow">{c("form")}</div>
          <form className="form" style={{ marginTop: 16, maxWidth: "none" }} onSubmit={submit} data-testid="contact-form">
            <div className="row">
              <label>{c("f")[0]}<input required data-testid="contact-name" value={form.name} onChange={set("name")} /></label>
              <label>{c("f")[1]}<input type="email" required data-testid="contact-email" value={form.email} onChange={set("email")} /></label>
            </div>
            <label>{c("f")[2]}
              <select data-testid="contact-topic" value={form.topic || c("topics")[0]} onChange={set("topic")}>
                {c("topics").map((x) => <option key={x}>{x}</option>)}
              </select>
            </label>
            <label>{c("f")[3]}<textarea rows="5" required data-testid="contact-message" value={form.message} onChange={set("message")} /></label>
            <button className="btn primary" type="submit" data-testid="contact-submit" disabled={busy || sent} style={{ justifySelf: "start" }}>
              {busy ? <span className="spin" /> : c("send")}
            </button>
            {sent && <div className="notice" data-testid="contact-success">{c("sent")}</div>}
          </form>
        </Reveal>
      </div>
    </>
  );
}

export function Account() {
  const { t, money } = useShop();
  const a = (k) => t(`account.${k}`);
  const [email, setEmail] = useState("");
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const r = await api(`/orders?email=${encodeURIComponent(email)}`);
      setOrders(r);
    } catch {
      toast(t("wholesale.err"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHd eb="Nattiva" h={a("h")} p={a("p")} testid="account-title" />
      <div className="wrap" style={{ maxWidth: 720, paddingBottom: 90 }}>
        <Reveal>
          <form className="lookup" style={{ maxWidth: "none" }} onSubmit={lookup} data-testid="account-lookup-form">
            <input type="email" required data-testid="account-email-input" placeholder={a("ph")} value={email} onChange={(e) => setEmail(e.target.value)} style={{ fontFamily: "var(--body)", textTransform: "none" }} />
            <button className="btn primary" type="submit" data-testid="account-lookup-button" disabled={busy}>
              {busy ? <span className="spin" /> : a("btn")}
            </button>
          </form>
          <p className="note">{a("note")}</p>
        </Reveal>
        {orders && (
          <div style={{ marginTop: 34 }} data-testid="account-orders">
            {orders.length === 0 ? (
              <p className="empty" data-testid="account-no-orders">{a("none")}</p>
            ) : (
              <>
                <div className="eyebrow" style={{ marginBottom: 14 }}>{a("orders")} ({orders.length})</div>
                {orders.map((o) => (
                  <div className="ordcard" key={o.id} data-testid={`order-${o.number}`}>
                    <div className="hd">
                      <span><code>{o.number}</code></span>
                      <span>{new Date(o.ts).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</span>
                      <span style={{ textTransform: "uppercase", letterSpacing: ".14em" }}>{o.status}</span>
                    </div>
                    {o.items.map((it) => (
                      <div className="tot" key={it.id}><span>{it.name} × {it.q}</span><span>{money(it.price * it.q)}</span></div>
                    ))}
                    <div className="tot big"><span>Total</span><span>{money(o.total)}</span></div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}

export function Checkout() {
  const { t, cart, subtotal, money, clearCart } = useShop();
  const c = (k) => t(`checkout.${k}`);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "", address: "", city: "", state: "", zip: "" });
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const ship = subtotal >= 250 || !subtotal ? 0 : 12;

  const submit = async (e) => {
    e.preventDefault();
    if (busy || !cart.length) return;
    setBusy(true);
    try {
      const r = await api("/orders", { method: "POST", body: JSON.stringify({ ...form, items: cart }) });
      setOrder(r);
      clearCart();
      toast(c("placed"));
    } catch {
      toast(t("wholesale.err"));
    } finally {
      setBusy(false);
    }
  };

  if (order) {
    return (
      <>
        <PageHd eb="Nattiva" h={c("placed")} testid="order-success-title" />
        <div className="wrap center" style={{ paddingBottom: 90 }}>
          <Reveal>
            <p className="lede" style={{ margin: "0 auto 10px" }}>{c("orderNo")}</p>
            <p style={{ fontFamily: "var(--mono)", fontSize: 22 }} data-testid="order-number">{order.number}</p>
            <p className="lede" style={{ margin: "10px auto 30px" }}>{money(order.total)} · {order.email}</p>
            <Link className="btn" to="/shop" data-testid="order-again-link">{c("again")}</Link>
          </Reveal>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHd eb="Nattiva" h={c("h")} testid="checkout-title" />
      <div className="wrap checkout" style={{ paddingBottom: 90 }}>
        <Reveal>
          <div>
            <div className="steps"><b>1 {c("steps")[0]}</b><span>2 {c("steps")[1]}</span><span>3 {c("steps")[2]}</span></div>
            <form className="form" style={{ maxWidth: "none" }} onSubmit={submit} data-testid="checkout-form">
              <label>{c("f")[0]}<input type="email" required data-testid="checkout-email" value={form.email} onChange={set("email")} /></label>
              <div className="row">
                <label>{c("f")[1]}<input required data-testid="checkout-first-name" value={form.first_name} onChange={set("first_name")} /></label>
                <label>{c("f")[2]}<input required data-testid="checkout-last-name" value={form.last_name} onChange={set("last_name")} /></label>
              </div>
              <label>{c("f")[3]}<input required data-testid="checkout-address" value={form.address} onChange={set("address")} /></label>
              <div className="row">
                <label>{c("f")[4]}<input required data-testid="checkout-city" value={form.city} onChange={set("city")} /></label>
                <label>{c("f")[5]}<input required data-testid="checkout-state" value={form.state} onChange={set("state")} /></label>
              </div>
              <label>{c("f")[6]}<input required data-testid="checkout-zip" style={{ maxWidth: 200 }} value={form.zip} onChange={set("zip")} /></label>
              <p className="note">{c("p")}</p>
              {!cart.length && <p className="note" data-testid="checkout-empty-note" style={{ borderColor: "var(--warn)" }}>{c("emptyNote")}</p>}
              <button className="btn primary" type="submit" data-testid="checkout-place-order" disabled={busy || !cart.length} style={{ justifySelf: "start" }}>
                {busy ? <span className="spin" /> : c("pay")}
              </button>
            </form>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <aside className="summary" data-testid="checkout-summary">
            <div className="eyebrow">{c("sum")}</div>
            {cart.length === 0 ? (
              <p className="empty">{t("cart.empty")}</p>
            ) : (
              cart.map((l) => {
                const p = P.find((x) => x.id === l.id);
                if (!p) return null;
                return (
                  <div className="line" key={l.id}>
                    <img src={M[p.img]} alt={p.n} />
                    <div><div className="n">{p.n}</div><small>{t("cart.qty")} {l.q}</small></div>
                    <div className="p">{money(p.price * l.q)}</div>
                  </div>
                );
              })
            )}
            <div style={{ marginTop: 16 }}>
              <div className="tot"><span>{t("cart.sub")}</span><span>{money(subtotal)}</span></div>
              <div className="tot"><span>{t("cart.ship")}</span><span>{ship ? money(ship) : t("cart.free")}</span></div>
              <div className="tot big"><span>Total</span><span data-testid="checkout-total">{money(subtotal + ship)}</span></div>
            </div>
          </aside>
        </Reveal>
      </div>
    </>
  );
}

export function NotFound() {
  return (
    <>
      <PageHd eb="404" h="Page not found" testid="notfound-title" />
      <div className="wrap center" style={{ paddingBottom: 90 }}>
        <Link className="btn" to="/" data-testid="notfound-home">Nattiva</Link>
      </div>
    </>
  );
}
